import { useCallback, useEffect, useState } from 'react';
import { Icon, Loader } from 'semantic-ui-react';
import { Document, Page, pdfjs } from 'react-pdf';
import useBreakpoint from 'hooks/useBreakpoint';
import usePager from 'hooks/usePager';
import styles from './docViewer.module.scss';
// @ts-ignore - asset import handled by webpack asset/resource rule
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;
interface IPDFViewerProps {
  src: string;
  controlClassName?: string;
}

const PDFViewer = ({ src, controlClassName }: IPDFViewerProps) => {
  const breakPoint = useBreakpoint();
  const { active, next, prev } = usePager(1);
  const [pages, setPages] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revokedUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        setBlobUrl(url);
      } catch {
        if (!cancelled) setBlobUrl(src);
      }
    })();
    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [src]);

  const handleLoadSuccess = useCallback(({ numPages }) => {
    setPages(numPages);
  }, []);

  const pageProps = { pageNumber: active, width: undefined, height: undefined };
  const isMobile = breakPoint === 'sm' || breakPoint === 'xs';
  if (isMobile) {
    pageProps.width = window.innerWidth - 40;
  } else {
    pageProps.height = window.innerHeight;
  }

  if (!blobUrl) {
    return <Loader active={true} />;
  }

  return (
    <Document
      file={blobUrl}
      onLoadSuccess={handleLoadSuccess}
      className={`${styles.document} fs-exclude`}
      loading={<Loader active={true} />}
    >
      <Page {...pageProps} />
      <div
        className={classnames(
          styles.pageControls,
          isMobile && styles.show,
          controlClassName,
        )}
      >
        <button type="button" disabled={active <= 1} onClick={prev}>
          <Icon name="angle left" size="large" />
        </button>
        <span>
          {active || (pages ? 1 : '--')} of {pages || '--'}
        </span>
        <button type="button" disabled={active >= pages} onClick={next}>
          <Icon name="angle right" size="large" />
        </button>
      </div>
    </Document>
  );
};

export default PDFViewer;
